/**
 * Package data type classes for Phase 2.1
 * Follows pattern from plugins/types.js
 * 
 * Store_Launcher_PackageData - Launcher package from store
 * Store_Tool_PackageData - Tool package from store
 */

// Base URL for store images
const STORE_API_URL = 'https://packages.bitbarn.io/api';

export class PackageData {
  static PACKAGE_TYPES = {
    APP_LAUNCHER: 'app_launcher',
    TOOL: 'tool'
  };

  static LICENSE_TYPES = {
    FREE: 'free',
    STUDIO: 'studio',
    COMMERCIAL: 'commercial',
    ENTERPRISE: 'enterprise'
  };

  constructor(data) {
    this.id = data.id;
    this.slug = data.slug || '';
    this.name = data.name || '';
    this.type = data.type;
    this.description = data.description || '';
    this.git_url = data.git_url || '';
    this.version = data.version || '1.0.0';
    this.license = data.license || 'free';
    
    // Phase 2.1: Author info
    this.author_name = data.author_name || '';
    this.author_email = data.author_email || '';
    this.author_url = data.author_url || '';
    
    // Phase 2.1: Media
    this.icon_id = data.icon_id || null;
    
    // Phase 2.1: Platform support
    this.target_app_id = data.target_app_id || null;
    this.supported_platforms = data.supported_platforms || [];
    this.supported_versions = data.supported_versions || [];
    
    // Phase 2.1: Features and config
    this.features = data.features || [];
    this.configuration_fields = data.configuration_fields || [];
    
    // Phase 2.1: Documentation
    this.user_wiki = data.user_wiki || null;
    this.dev_wiki = data.dev_wiki || null;
    
    // Phase 2.1: Dependencies
    this.requirements = data.requirements || [];
    
    // Staging state
    this.is_staged = data.is_staged ?? false;
    
    // Legacy
    this.manifest = data.manifest || {};
    this.last_synced = data.last_synced;
  }

  get isAppLauncher() {
    return this.type === PackageData.PACKAGE_TYPES.APP_LAUNCHER;
  }

  get isTool() {
    return this.type === PackageData.PACKAGE_TYPES.TOOL;
  }
  
  get iconUrl() {
    if (!this.icon_id) return null;
    return `${STORE_API_URL}/images/${this.icon_id}`;
  }
  
  get hasAuthor() {
    return Boolean(this.author_name);
  }
  
  get hasFeatures() {
    return this.features && this.features.length > 0;
  }
  
  get hasSupportedVersions() {
    return this.supported_versions && this.supported_versions.length > 0;
  }
  
  get hasUserWiki() {
    return Boolean(this.user_wiki);
  }
  
  get hasDevWiki() {
    return Boolean(this.dev_wiki);
  }
  
  get licenseBadgeColor() {
    switch (this.license) {
      case 'free':
        return 'bg-green-500/20 text-green-400';
      case 'studio':
        return 'bg-blue-500/20 text-blue-400';
      case 'commercial':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'enterprise':
        return 'bg-purple-500/20 text-purple-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  }

  static fromApiResponse(data) {
    return new PackageData(data);
  }

  static fromApiResponseList(dataList = []) {
    return dataList.map(PackageData.fromApiResponse);
  }
}

// Alias for clarity
export const LauncherPackageData = PackageData;
export const ToolPackageData = PackageData;

export class StagedPackageData {
  constructor(data) {
    this.id = data.id;
    this.remote_package_id = data.remote_package_id;
    this.staged_at = data.staged_at;
    this.staged_by = data.staged_by;
    this.enabled = data.enabled ?? true;
    this.package = data.package ? new PackageData(data.package) : null;
  }

  static fromApiResponse(data) {
    return new StagedPackageData(data);
  }

  static fromApiResponseList(dataList = []) {
    return dataList.map(StagedPackageData.fromApiResponse);
  }
}

/**
 * Application registry data
 */
export class ApplicationData {
  constructor(data) {
    this.id = data.id;
    this.slug = data.slug || '';
    this.name = data.name || '';
    this.vendor = data.vendor || '';
    this.category = data.category || '';
    this.versions = data.versions || {};
    this.icon_id = data.icon_id || null;
  }
  
  get iconUrl() {
    if (!this.icon_id) return null;
    return `${STORE_API_URL}/images/${this.icon_id}`;
  }
  
  get versionList() {
    return Object.keys(this.versions || {});
  }

  static fromApiResponse(data) {
    return new ApplicationData(data);
  }

  static fromApiResponseList(dataList = []) {
    return dataList.map(ApplicationData.fromApiResponse);
  }
}
